import type { ReactNode } from 'react';
import {
  isTextRun,
  STATIC_PAGE_RICH_TEXT,
  type RichBlockNode,
  type RichDoc,
  type RichListItem,
  type RichRun,
} from '../../api/staticPageRichText';
import { assetSrc } from '../../api/staticPageBlocks';
import './RichDocContent.scss';

interface Props {
  doc: RichDoc;
  assetUrls?: Record<string, string>;
  className?: string;
}

// Server-side validation enforces the same allowlist; this guard keeps a doc
// that predates or bypasses it from scripting the public page (parity with
// react-markdown's URL sanitizing on the legacy path). Built from the pin
// (module scope) so it can't drift from the backend's contract.
const SAFE_LINK_RE = new RegExp(STATIC_PAGE_RICH_TEXT.linkPattern);

const renderRun = (run: RichRun, key: number, assetUrls?: Record<string, string>): ReactNode => {
  if (!isTextRun(run)) {
    const src = assetSrc(run.image.asset_ref, assetUrls);
    if (!src) return <span key={key} className="rich-doc__missing-image" />;
    return <img key={key} className="rich-doc__image" src={src} alt={run.image.alt ?? ''} />;
  }

  let node: ReactNode = run.text;
  const marks = run.marks ?? [];
  if (marks.includes('code')) node = <code>{node}</code>;
  if (marks.includes('italic')) node = <em>{node}</em>;
  if (marks.includes('bold')) node = <strong>{node}</strong>;
  node = (
    <span
      key={key}
      className={run.size ? `rt-size-${run.size}` : undefined}
      style={run.color ? { color: `#${run.color}` } : undefined}
    >
      {node}
    </span>
  );
  if (run.link && SAFE_LINK_RE.test(run.link)) {
    node = (
      <a key={key} href={run.link}>
        {node}
      </a>
    );
  }
  return node;
};

const renderRuns = (runs: RichRun[], assetUrls?: Record<string, string>) =>
  runs.map((run, i) => renderRun(run, i, assetUrls));

const renderItems = (items: RichListItem[], style: 'bullet' | 'ordered', assetUrls?: Record<string, string>) => {
  const children = items.map((item, i) => (
    <li key={i}>
      {renderRuns(item.runs, assetUrls)}
      {item.items && item.items.length > 0 && renderItems(item.items, style, assetUrls)}
    </li>
  ));
  return style === 'ordered' ? <ol>{children}</ol> : <ul>{children}</ul>;
};

const alignClass = (align?: string) => (align ? `rt-align-${align}` : undefined);

const renderBlock = (block: RichBlockNode, key: number, assetUrls?: Record<string, string>): ReactNode => {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p key={key} className={alignClass(block.align)}>
          {renderRuns(block.runs, assetUrls)}
        </p>
      );
    case 'heading': {
      const Heading = block.level === 2 ? 'h2' : 'h3';
      return (
        <Heading key={key} className={alignClass(block.align)}>
          {renderRuns(block.runs, assetUrls)}
        </Heading>
      );
    }
    case 'list':
      return <div key={key}>{renderItems(block.items, block.style, assetUrls)}</div>;
    case 'quote':
      return <blockquote key={key}>{renderRuns(block.runs, assetUrls)}</blockquote>;
    case 'code':
      return (
        <pre key={key}>
          <code>{block.text}</code>
        </pre>
      );
    case 'hr':
      return <hr key={key} />;
    default:
      // Unknown kind from a newer format version, or from parseBlockDocument's
      // shallow validation letting unexpected runtime data through: render
      // nothing rather than crash the whole page.
      return null;
  }
};

const RichDocContent = ({ doc, assetUrls, className = '' }: Props) => (
  <div className={`rich-doc ${className}`}>{doc.blocks.map((block, i) => renderBlock(block, i, assetUrls))}</div>
);

export default RichDocContent;
